import { useState } from "react";
import { useTracker } from "@ferrivia/auth-web";
import { useMarket, useTranslation } from "@ferrivia/i18n";
import { Button, Text } from "@ferrivia/ui";
import type { QuestionnaireSummaryListItem, ReadJourneyStep } from "@ferrivia/api-contract";
import { AnalysisPurchaseSheet } from "../../../components/AnalysisPurchase";
import { JourneySteps, type JourneyStepsCopy } from "../../../components/JourneySteps";
import { coachBookingUrl } from "../../../lib/bookingLinks";
import { AnalysisOffer } from "./AnalysisOffer";
import { LabSourcesSheet } from "./LabSourcesSheet";
import s from "./SupportSteps.module.css";

/* The Asiantuntijatuki tab's steps card: recent labs, the health
 * questionnaire, then booking a call with Mari. The card itself is
 * JourneySteps; this holds the tab's copy, its analytics event and the
 * outbound booking link.
 *
 * Buying the alkukartoitus is not a step — booking is gated on it, which is
 * a different thing from being one. While unpaid the card is headed by the
 * offer instead (titled with the product, its steps then read as what
 * happens after ordering), and the offer's order button is the card's one
 * primary control. Once paid the card is the plain steps card again.
 *
 * Outside Finland there is no offer: the card is always the plain steps
 * card, and booking is gated on labs and the screener alone (the server's
 * supportSteps). In the US there is nothing of ours to buy either, so the
 * labs step also points to where results can be ordered (LabSourcesSheet). */
export function SupportSteps({
  steps,
  summaries,
  refetch,
  assessmentPaid,
}: {
  steps: ReadJourneyStep[];
  summaries?: readonly QuestionnaireSummaryListItem[];
  refetch: () => void;
  assessmentPaid: boolean;
}) {
  const { t } = useTranslation("client");
  const tracker = useTracker();
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [labSourcesOpen, setLabSourcesOpen] = useState(false);
  const market = useMarket();
  const commerce = market === "fi";
  const plain = assessmentPaid || !commerce;

  const stepsTitle = t("support.steps.title", "Näin alkukartoitus etenee");

  const copy: JourneyStepsCopy = {
    eyebrow: plain
      ? t("support.steps.eyebrow", "Ennen puhelua Marin kanssa")
      : t("support.offer.eyebrow", "Aloita tästä"),
    title: plain ? stepsTitle : t("store.products.analysis.name", "Raudanpuutteen alkukartoitus"),
    steps: {
      labs: {
        label: t("support.steps.labs.label", "Verikokeet"),
        body: {
          todo: t(
            "support.steps.labs.todo",
            "Alkukartoitukseen tarvitaan enintään kuukauden vanhat verikokeet. Voit tilata laboratoriopaketin meiltä tai lisätä omat tuloksesi.",
          ),
          in_progress: t(
            "support.steps.labs.inProgress",
            "Laboratoriopakettisi on tilattu. Tämä vaihe täyttyy, kun näytteesi on otettu ja laboratorio on raportoinut sen.",
          ),
          done: t("support.steps.labs.done", "Verikokeesi on otettu viimeisen kuukauden aikana."),
        },
        storeCta: t("support.steps.labs.storeCta", "Tilaa laboratoriopaketti"),
        uploadCta: t("support.steps.labs.uploadCta", "Lisää omat tulokset"),
      },
      screener: {
        label: t("support.steps.screener.label", "Esitietokysely"),
        body: {
          todo: t(
            "support.steps.screener.todo",
            "Täytä esitietokysely, jotta Mari voi tutustua tilanteeseesi ennen puhelua.",
          ),
          in_progress: t("support.steps.screener.inProgress", "Jatka esitietojen täyttämistä siitä mihin jäit."),
          done: t("support.steps.screener.done", "Esitietosi on täytetty."),
        },
      },
      booking: {
        label: t("support.steps.booking.label", "Puhelu Marin kanssa"),
        body: {
          locked: t(
            "support.steps.booking.locked",
            "Kun alkukartoitus on tilattu, verikokeet otettu ja esitiedot täytetty, voit varata puheluajan Marin kalenterista.",
          ),
          todo: t("support.steps.booking.todo", "Varaa sinulle sopiva puheluaika Marin kalenterista."),
        },
      },
    },
  };

  return (
    <>
      <JourneySteps
        steps={steps}
        summaries={summaries}
        refetch={refetch}
        copy={copy}
        track={(cta) => tracker.track("support.step_cta_clicked", { cta })}
        summaryPath="support"
        muted={!plain}
        labsExtra={
          market === "us" ? (
            <Button
              variant="secondary"
              onClick={() => {
                tracker.track("support.step_cta_clicked", { cta: "labs_find" });
                setLabSourcesOpen(true);
              }}
            >
              {t("support.steps.labs.findCta", "Mistä saan verikokeet?")}
            </Button>
          ) : undefined
        }
        lead={
          !commerce ? undefined : assessmentPaid ? (
            <Text as="p" size="md" className={s.paid}>
              {t("support.offer.paid", "Alkukartoituksesi on tilattu.")}
            </Text>
          ) : (
            <AnalysisOffer
              stepsTitle={stepsTitle}
              onOrder={() => {
                tracker.track("support.step_cta_clicked", { cta: "purchase" });
                setPurchaseOpen(true);
              }}
            />
          )
        }
        // Booking only unlocks once labs, the screener and (in Finland) the
        // payment are done, when nothing else is primary, so it is always the
        // lead step and only needs the primary look.
        renderBooking={() => (
          <a
            className={s.bookLink}
            href={coachBookingUrl(market)}
            target="_blank"
            rel="noreferrer"
            onClick={() => tracker.track("support.step_cta_clicked", { cta: "book" })}
          >
            {t("support.steps.booking.cta", "Varaa puheluaika")}
          </a>
        )}
      />
      {commerce && <AnalysisPurchaseSheet open={purchaseOpen} onOpenChange={setPurchaseOpen} />}
      {market === "us" && <LabSourcesSheet open={labSourcesOpen} onOpenChange={setLabSourcesOpen} />}
    </>
  );
}
